import React, { useEffect, useRef, useState } from "react";
import { User } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { useGoogleFont } from "../hooks/useGoogleFont.js";

const StreamingMarkdown = ({ content, isStreaming, isDark = false, isRtl = false, textColor, fontFamily, fontSize }) => {
  const [displayedContent, setDisplayedContent] = useState("");
  const [showCursor, setShowCursor] = useState(false);
  const contentRef = useRef(content);
  const indexRef = useRef(0);
  const animationFrameRef = useRef(null);
  const lastUpdateRef = useRef(0);

  useEffect(() => {
    // Reset if content changes significantly
    if (content !== contentRef.current) {
      contentRef.current = content;
      // If we're already past the new content length, show it all
      if (indexRef.current >= content.length) {
        setDisplayedContent(content);
        setShowCursor(false);
        return;
      }
      // If new content is shorter, reset
      if (content.length < displayedContent.length) {
        indexRef.current = 0;
        setDisplayedContent("");
      }
    }

    if (!isStreaming) {
      // Not streaming - show full content immediately
      setDisplayedContent(content);
      setShowCursor(false);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      return;
    }

    // Streaming mode - animate character by character
    setShowCursor(true);
    const animate = (timestamp) => {
      // Throttle updates for smooth 60fps animation
      if (timestamp - lastUpdateRef.current < 16) {
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      lastUpdateRef.current = timestamp;

      if (indexRef.current < content.length) {
        // Add 2-5 characters per frame for natural speed
        const charsToAdd = Math.max(2, Math.floor(Math.random() * 4));
        const newIndex = Math.min(indexRef.current + charsToAdd, content.length);
        setDisplayedContent(content.slice(0, newIndex));
        indexRef.current = newIndex;
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Finished streaming
        setShowCursor(false);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [content, isStreaming]);

  return (
    <div
      className="relative"
      style={{
        direction: isRtl ? "rtl" : "ltr",
        textAlign: isRtl ? "right" : "left",
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        className="prose prose-sm max-w-none [&>*:last-child]:!mb-0 [&>*:first-child]:!mt-0"
        style={{
          color: textColor || (isDark ? "#f3f4f6" : "#1A1A1A"),
          fontFamily: fontFamily || "Inter",
          fontSize: fontSize || "14px",
          margin: 0,
          padding: 0,
        }}
        components={{
          a({ node, children, ...props }) {
            return (
              <a
                className={
                  isDark ? "text-blue-400 hover:text-blue-300 hover:underline" : "text-blue-600 hover:underline"
                }
                target="_blank"
                rel="noopener noreferrer"
                {...props}
              >
                {children}
              </a>
            );
          },
          p({ node, children, ...props }) {
            return (
              <p
                className="leading-relaxed [&:not(:last-child)]:mb-2 [&:first-child]:!mt-0"
                style={{
                  color: textColor || (isDark ? "#f3f4f6" : "#1A1A1A"),
                  fontFamily: fontFamily || "Inter",
                  fontSize: fontSize || "14px",
                  margin: 0,
                  marginTop: 0,
                  padding: 0,
                }}
                {...props}
              >
                {children}
              </p>
            );
          },
          ul({ node, children, ...props }) {
            return (
              <ul className="list-disc pl-5 space-y-1 [&:first-child]:!mt-0" style={{ marginTop: 0 }} {...props}>
                {children}
              </ul>
            );
          },
          ol({ node, children, ...props }) {
            return (
              <ol className="list-decimal pl-5 space-y-1 [&:first-child]:!mt-0" style={{ marginTop: 0 }} {...props}>
                {children}
              </ol>
            );
          },
          li({ node, children, ...props }) {
            return (
              <li
                className="mb-1"
                style={{
                  color: textColor || (isDark ? "#f3f4f6" : "#1A1A1A"),
                  fontFamily: fontFamily || "Inter",
                  fontSize: fontSize || "14px",
                }}
                {...props}
              >
                {children}
              </li>
            );
          },
          h1({ node, children, ...props }) {
            return (
              <h1
                className="text-xl font-bold mb-2 [&:first-child]:!mt-0"
                style={{
                  color: textColor || (isDark ? "#f3f4f6" : "#1A1A1A"),
                  fontFamily: fontFamily || "Inter",
                  marginTop: "0.75rem",
                }}
                {...props}
              >
                {children}
              </h1>
            );
          },
          h2({ node, children, ...props }) {
            return (
              <h2
                className="text-lg font-semibold mb-2 [&:first-child]:!mt-0"
                style={{
                  color: textColor || (isDark ? "#f3f4f6" : "#1A1A1A"),
                  fontFamily: fontFamily || "Inter",
                  marginTop: "0.75rem",
                }}
                {...props}
              >
                {children}
              </h2>
            );
          },
          h3({ node, children, ...props }) {
            return (
              <h3
                className="text-base font-semibold mb-2 [&:first-child]:!mt-0"
                style={{
                  color: textColor || (isDark ? "#f3f4f6" : "#1A1A1A"),
                  fontFamily: fontFamily || "Inter",
                  marginTop: "0.75rem",
                }}
                {...props}
              >
                {children}
              </h3>
            );
          },
          blockquote({ node, children, ...props }) {
            return (
              <blockquote
                className={`border-l-4 pl-4 italic ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"
                  }`}
                {...props}
              >
                {children}
              </blockquote>
            );
          },
          table({ node, children, ...props }) {
            return (
              <div className="overflow-x-auto my-3">
                <table className="min-w-full border-collapse border border-gray-300" {...props}>
                  {children}
                </table>
              </div>
            );
          },
          th({ node, children, ...props }) {
            return (
              <th className="border border-gray-300 bg-gray-100 px-3 py-2 text-left font-semibold" {...props}>
                {children}
              </th>
            );
          },
          td({ node, children, ...props }) {
            return (
              <td className="border border-gray-300 px-3 py-2" {...props}>
                {children}
              </td>
            );
          },
          code({ className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || "");
            if (match) {
              return (
                <SyntaxHighlighter
                  style={oneDark}
                  language={match[1]}
                  PreTag="div"
                  wrapLines
                  {...props}
                >
                  {String(children).replace(/\n$/, "")}
                </SyntaxHighlighter>
              );
            }
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
        }}
      >
        {displayedContent}
      </ReactMarkdown>
      {showCursor && (
        <span
          className="inline-block w-1.5 h-4 bg-blue-500 ml-0.5 animate-pulse align-text-bottom"
          style={{ animationDuration: "0.8s" }}
        />
      )}
    </div>
  );
};

const TypingIndicator = ({ isDark }) => (
  <div className="flex gap-1.5">
    <div
      className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
      style={{ animationDelay: "0ms" }}
    ></div>
    <div
      className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
      style={{ animationDelay: "150ms" }}
    ></div>
    <div
      className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
      style={{ animationDelay: "300ms" }}
    ></div>
  </div>
);

// Decision widget component for HITL (Human-in-the-Loop)
const DecisionWidget = ({
  question,
  options,
  onDecision,
  disabled,
  isDark = false,
}) => {
  return (
    <div className={`mt-3 p-3 rounded-lg border ${isDark ? "bg-blue-900/30 border-blue-700/50" : "bg-blue-50 border-blue-200"
      }`}>
      <p className={`text-sm font-medium mb-2 ${isDark ? "text-gray-100" : "text-gray-900"}`}>
        {question}
      </p>
      <div className="space-y-2">
        {options.map((option) => (
          <button
            key={option.id}
            onClick={() => onDecision(option.id, option.label)}
            disabled={disabled}
            className={`w-full text-left px-3 py-2 text-sm rounded transition-all ${isDark
              ? "bg-gray-800 border border-gray-700 hover:bg-blue-900/50 hover:border-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              : "bg-white border border-gray-300 hover:bg-blue-50 hover:border-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
              }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const AssistantMessage = ({
  message,
  formatTime,
  isDark,
  primaryColor,
  agentMessageBubbleColor,
  textColor,
  assistantTextColor,
  fontFamily,
  agentMessageBubbleFontFamily,
  agentMessageBubbleFontSize,
  fontSize,
  isRtl,
  onDecision,
  companyLogo,
  conciergeName,
  companyName,
}) => {
  const displayContent = typeof message.content === "string" ? message.content : "";
  const isTypingMessage = message.isProcessing && (!displayContent || displayContent.length === 0);
  const bubbleColor = agentMessageBubbleColor !== undefined && agentMessageBubbleColor !== null
    ? agentMessageBubbleColor
    : (isDark ? "#1f2937" : "#FFFFFF");
  const messageTextColor = assistantTextColor !== undefined && assistantTextColor !== null
    ? assistantTextColor
    : (textColor !== undefined && textColor !== null
      ? textColor
      : (isDark ? "#f3f4f6" : "#1A1A1A"));
  const messageFontFamily = agentMessageBubbleFontFamily !== undefined && agentMessageBubbleFontFamily !== null
    ? agentMessageBubbleFontFamily
    : (fontFamily !== undefined && fontFamily !== null ? fontFamily : "Inter");
  const messageFontSize = agentMessageBubbleFontSize !== undefined && agentMessageBubbleFontSize !== null
    ? agentMessageBubbleFontSize
    : (fontSize !== undefined && fontSize !== null ? fontSize : "14px");

  useGoogleFont(messageFontFamily);

  return (
    <div className="flex gap-3 flex-row animate-in">
      <div className="flex-shrink-0 mt-1">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center overflow-hidden ${isDark ? "bg-gray-700" : "bg-gray-200"
            }`}
        >
          {companyLogo ? (
            <div className="w-full h-full flex items-center justify-center">
              <img
                src={companyLogo}
                alt="Company logo"
                className="w-full h-full object-cover rounded-full"
                style={{ maxWidth: "100%", maxHeight: "100%" }}
              />
            </div>
          ) : (
            <div
              className="w-full h-full flex items-center justify-center text-white font-semibold"
              style={{
                backgroundColor: primaryColor,
                fontSize: "14px",
                fontFamily: fontFamily || "Inter",
              }}
            >
              {(conciergeName || "A").charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col items-start max-w-[75%]">
        <div
          className={`rounded-2xl px-4 py-2.5 shadow-sm border rounded-tl-sm ${isDark ? "border-gray-700" : "border-gray-200"
            }`}
          style={{
            backgroundColor: bubbleColor,
            color: messageTextColor,
            fontFamily: messageFontFamily,
            fontSize: messageFontSize,
            direction: isRtl ? "rtl" : "ltr",
            textAlign: isRtl ? "right" : "left",
          }}
        >
          {isTypingMessage ? (
            <TypingIndicator isDark={isDark} />
          ) : (
            <>
              {message.requiresDecision && message.decisionData && onDecision ? (
                <div>
                  {displayContent && (
                    <div className="mb-3">
                      <StreamingMarkdown
                        content={displayContent}
                        isStreaming={message.isProcessing}
                        isDark={isDark}
                        isRtl={isRtl}
                        textColor={messageTextColor}
                        fontFamily={messageFontFamily}
                        fontSize={messageFontSize}
                      />
                    </div>
                  )}
                  <DecisionWidget
                    question={message.decisionData.question}
                    options={message.decisionData.options}
                    onDecision={(optionId, optionValue) =>
                      onDecision(message.decisionData.decisionId, optionId, optionValue)
                    }
                    disabled={!!message.decisionResponse}
                    isDark={isDark}
                  />
                  {message.decisionResponse && (
                    <div className={`mt-2 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      Selected: {message.decisionResponse.selectedOption}
                    </div>
                  )}
                </div>
              ) : displayContent ? (
                <StreamingMarkdown
                  content={displayContent}
                  isStreaming={message.isProcessing}
                  isDark={isDark}
                  isRtl={isRtl}
                  textColor={messageTextColor}
                  fontFamily={messageFontFamily}
                  fontSize={messageFontSize}
                />
              ) : null}
            </>
          )}
        </div>
        <p className={`text-[11px] mt-1.5 px-1 ${isDark ? "text-gray-500" : "text-gray-400"}`} dir="ltr">
          {formatTime(message.timestamp)}
        </p>
      </div>
    </div>
  );
};

const UserMessage = ({
  message,
  formatTime,
  userMessageBoxColor,
  primaryColor,
  textColor,
  userTextColor,
  fontFamily,
  userMessageBubbleFontFamily,
  userMessageBubbleFontSize,
  fontSize,
  isDark,
  isRtl,
}) => {
  const primary = userMessageBoxColor !== undefined && userMessageBoxColor !== null
    ? userMessageBoxColor
    : (primaryColor !== undefined && primaryColor !== null ? primaryColor : "#2563eb");
  const messageTextColor = userTextColor !== undefined && userTextColor !== null
    ? userTextColor
    : (textColor !== undefined && textColor !== null ? textColor : "#FFFFFF");
  const messageFontFamily = userMessageBubbleFontFamily !== undefined && userMessageBubbleFontFamily !== null
    ? userMessageBubbleFontFamily
    : (fontFamily !== undefined && fontFamily !== null ? fontFamily : "Inter");
  const messageFontSize = userMessageBubbleFontSize !== undefined && userMessageBubbleFontSize !== null
    ? userMessageBubbleFontSize
    : (fontSize !== undefined && fontSize !== null ? fontSize : "14px");

  useGoogleFont(messageFontFamily);

  return (
    <div className="flex gap-3 flex-row-reverse animate-in">
      <div className="flex-shrink-0 mt-1">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? "bg-gray-700" : "bg-gray-200"
            }`}
        >
          <User className="w-4 h-4 text-gray-500" />
        </div>
      </div>

      <div className="flex flex-col items-end max-w-[75%]">
        <div
          className="rounded-2xl px-4 py-2.5 shadow-lg rounded-tr-sm"
          style={{
            background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 100%)`,
            fontFamily: messageFontFamily,
            textAlign: isRtl ? "right" : "left",
            direction: isRtl ? "rtl" : "ltr",
          }}
        >
          <p
            className="whitespace-pre-wrap break-words"
            style={{
              color: messageTextColor,
              fontFamily: messageFontFamily,
              fontSize: messageFontSize,
              lineHeight: "1.5",
              textAlign: isRtl ? "right" : "left",
              margin: 0,
              padding: 0,
            }}
          >
            {message.content}
          </p>
        </div>
        <p className={`text-[11px] mt-1.5 px-1 ${isDark ? "text-gray-500" : "text-gray-400"}`} dir="ltr">
          {formatTime(message.timestamp)}
        </p>
      </div>
    </div>
  );
};

const MessageBubble = ({
  message,
  isDark,
  primaryColor,
  agentMessageBubbleColor,
  userMessageBoxColor,
  textColor,
  assistantTextColor,
  userTextColor,
  fontFamily,
  agentMessageBubbleFontFamily,
  agentMessageBubbleFontSize,
  userMessageBubbleFontFamily,
  userMessageBubbleFontSize,
  fontSize,
  formatTime,
  isRtl,
  onDecision,
  companyLogo,
  conciergeName,
  companyName,
}) => {
  if (!message) {
    return null;
  }

  if (message.role === "user") {
    return (
      <UserMessage
        message={message}
        formatTime={formatTime}
        userMessageBoxColor={userMessageBoxColor}
        primaryColor={primaryColor}
        textColor={textColor}
        userTextColor={userTextColor}
        fontFamily={fontFamily}
        userMessageBubbleFontFamily={userMessageBubbleFontFamily}
        userMessageBubbleFontSize={userMessageBubbleFontSize}
        fontSize={fontSize}
        isDark={isDark}
        isRtl={isRtl}
      />
    );
  }

  return (
    <AssistantMessage
      message={message}
      formatTime={formatTime}
      isDark={isDark}
      primaryColor={primaryColor}
      agentMessageBubbleColor={agentMessageBubbleColor}
      textColor={textColor}
      assistantTextColor={assistantTextColor}
      fontFamily={fontFamily}
      agentMessageBubbleFontFamily={agentMessageBubbleFontFamily}
      agentMessageBubbleFontSize={agentMessageBubbleFontSize}
      fontSize={fontSize}
      isRtl={isRtl}
      onDecision={onDecision}
      companyLogo={companyLogo}
      conciergeName={conciergeName}
      companyName={companyName}
    />
  );
};

export default MessageBubble;
